/**
 * Inbox data hooks — PEL-836 PR A.
 *
 * The consumer inbox unifies two feeds:
 *   1. Notifications (InboxApi / PEL-639): one-way system messages
 *      (order updates, promotions, engagement pings).
 *   2. Support conversations (SupportApi): two-way chat threads.
 *
 * `useInboxUnreadCount` is also the primary badge source for the Sidebar /
 * BottomTabBar. It:
 *   - Seeds from REST (`inbox.getUnreadCount`) on mount.
 *   - Subscribes to `chatService` `unread_count` events so the badge updates
 *     in real-time when new messages arrive on any conversation.
 *
 * We deliberately keep support conversations and notifications in separate
 * query keys. The InboxPage merges them into a unified display list in UI
 * code — this keeps invalidation scoped (marking a notification read should
 * NOT invalidate the conversations list and vice versa).
 */

import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  ConversationsResponse,
  GetInboxMessagesParams,
  InboxCategory,
  InboxListResponse,
  SupportConversation,
} from '@pelipost/consumer-api-client';

import { api } from '../lib/api/client';
import { chatService, type UnreadCountPayload } from '../lib/chatService';

// ────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────

export const inboxKeys = {
  all: ['inbox'] as const,
  messages: (params?: GetInboxMessagesParams) =>
    ['inbox', 'messages', params ?? {}] as const,
  unreadCount: () => ['inbox', 'unread-count'] as const,
  supportConversations: () => ['inbox', 'support-conversations'] as const,
};

// ────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────

/** Paginated notification feed (system / order / promotion / engagement / general). */
export function useInboxMessages(
  params?: GetInboxMessagesParams,
  options?: Omit<UseQueryOptions<InboxListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<InboxListResponse>({
    queryKey: inboxKeys.messages(params),
    queryFn: () => api.inbox.getMessages(params),
    ...options,
  });
}

/** User's support conversations (two-way chat with Pelipost support). */
export function useSupportConversations(
  options?: Omit<UseQueryOptions<ConversationsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ConversationsResponse>({
    queryKey: inboxKeys.supportConversations(),
    queryFn: () => api.support.getConversations(),
    // Fall back to an empty list on failure so the list screen still renders.
    retry: 1,
    ...options,
  });
}

/**
 * Live unread-count hook. Seeds from REST on mount, then stays current via
 * `chatService.unread_count` events. Returns `count` as a convenience.
 */
export function useInboxUnreadCount(
  options?: Omit<
    UseQueryOptions<{ count: number }>,
    'queryKey' | 'queryFn'
  >,
) {
  const qc = useQueryClient();

  const query = useQuery<{ count: number }>({
    queryKey: inboxKeys.unreadCount(),
    queryFn: () =>
      api.inbox.getUnreadCount().catch(() => ({ count: 0 })),
    staleTime: 30_000,
    ...options,
  });

  useEffect(() => {
    const onUnread = (payload: UnreadCountPayload) => {
      qc.setQueryData<{ count: number }>(inboxKeys.unreadCount(), {
        count: payload.count,
      });
    };
    chatService.on('unread_count', onUnread);
    return () => {
      chatService.off('unread_count', onUnread);
    };
  }, [qc]);

  return query;
}

// ────────────────────────────────────────────────────────────────
// Mutations
// ────────────────────────────────────────────────────────────────

/** Mark a single notification as read. Optimistic. */
export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string, { prev?: InboxListResponse }>({
    mutationFn: (messageId) => api.inbox.markRead(messageId),
    onMutate: async (messageId) => {
      await qc.cancelQueries({ queryKey: inboxKeys.all });
      // Optimistic: walk every cached /inbox/messages key and flip the flag.
      const caches = qc.getQueriesData<InboxListResponse>({
        queryKey: ['inbox', 'messages'],
      });
      caches.forEach(([key, data]) => {
        if (!data) return;
        qc.setQueryData<InboxListResponse>(key, {
          ...data,
          messages: data.messages.map((m) =>
            m.id === messageId
              ? { ...m, isRead: true, readAt: new Date().toISOString() }
              : m,
          ),
          unreadCount: Math.max(0, (data.unreadCount ?? 0) - 1),
        });
      });
      const unread = qc.getQueryData<{ count: number }>(
        inboxKeys.unreadCount(),
      );
      if (unread) {
        qc.setQueryData(inboxKeys.unreadCount(), {
          count: Math.max(0, unread.count - 1),
        });
      }
      return {};
    },
    onError: () => {
      // Refetch everything on failure to restore truth.
      qc.invalidateQueries({ queryKey: inboxKeys.all });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: inboxKeys.unreadCount() });
    },
  });
}

/** Mark every notification as read in one shot. */
export function useMarkAllInboxRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => api.inbox.markAllRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: inboxKeys.all });
      const caches = qc.getQueriesData<InboxListResponse>({
        queryKey: ['inbox', 'messages'],
      });
      const now = new Date().toISOString();
      caches.forEach(([key, data]) => {
        if (!data) return;
        qc.setQueryData<InboxListResponse>(key, {
          ...data,
          messages: data.messages.map((m) => ({
            ...m,
            isRead: true,
            readAt: m.readAt ?? now,
          })),
          unreadCount: 0,
        });
      });
      qc.setQueryData(inboxKeys.unreadCount(), { count: 0 });
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });
}

/** Delete a notification. Optimistic. */
export function useDeleteInboxMessage() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (messageId) => api.inbox.deleteMessage(messageId),
    onMutate: async (messageId) => {
      await qc.cancelQueries({ queryKey: inboxKeys.all });
      const caches = qc.getQueriesData<InboxListResponse>({
        queryKey: ['inbox', 'messages'],
      });
      caches.forEach(([key, data]) => {
        if (!data) return;
        const removed = data.messages.find((m) => m.id === messageId);
        qc.setQueryData<InboxListResponse>(key, {
          ...data,
          messages: data.messages.filter((m) => m.id !== messageId),
          total: Math.max(0, data.total - 1),
          unreadCount:
            removed && !removed.isRead
              ? Math.max(0, data.unreadCount - 1)
              : data.unreadCount,
        });
      });
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });
}

// ────────────────────────────────────────────────────────────────
// Unified item model for the list UI
// ────────────────────────────────────────────────────────────────

export type UnifiedItemSource = 'inbox' | 'support';

export interface UnifiedInboxItem {
  id: string;
  source: UnifiedItemSource;
  title: string;
  body: string;
  category: InboxCategory | 'support_conversation';
  isRead: boolean;
  deepLink: string | null;
  conversationId?: string;
  createdAt: string;
}

/**
 * Merge notifications + support conversations into a single reverse-chronological
 * feed. Unread-first, then most-recent-first inside each half.
 */
export function useUnifiedInbox(category?: InboxCategory) {
  const messagesQuery = useInboxMessages(
    category ? { page: 1, limit: 50, category } : { page: 1, limit: 50 },
  );
  const conversationsQuery = useSupportConversations();

  const items: UnifiedInboxItem[] = [];
  const messages = messagesQuery.data?.messages ?? [];
  const conversations: SupportConversation[] =
    conversationsQuery.data?.data ?? [];

  messages.forEach((m) =>
    items.push({
      id: m.id,
      source: 'inbox',
      title: m.title,
      body: m.body,
      category: m.category,
      isRead: m.isRead,
      deepLink: m.deepLink,
      createdAt: m.createdAt,
    }),
  );

  // For a support conversation, "title" is the subject and "body" is the most
  // recent customer/agent message body. isRead = has no unseen agent messages
  // on the customer side (we approximate with the conversation `updatedAt`).
  conversations.forEach((c) => {
    const last = c.messages?.[c.messages.length - 1];
    items.push({
      id: `support-${c.id}`,
      source: 'support',
      title: c.subject,
      body: last?.body ?? '(no messages)',
      category: 'support_conversation',
      // Consider a support conversation "read" unless the newest message is
      // from the agent and hasn't been read by the customer yet. Without a
      // per-customer read timestamp we default to marking it unread when
      // the latest message is from the agent. This matches mobile's
      // approximation and will be refined when the backend exposes a
      // per-side read cursor.
      isRead: !(last && last.senderType === 'agent'),
      deepLink: `/app/inbox/${c.id}`,
      conversationId: c.id,
      createdAt: c.updatedAt ?? c.createdAt,
    });
  });

  items.sort((a, b) => {
    if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
    return (
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  });

  const isLoading = messagesQuery.isLoading || conversationsQuery.isLoading;
  const refetch = useCallback(() => {
    messagesQuery.refetch();
    conversationsQuery.refetch();
  }, [messagesQuery, conversationsQuery]);

  return {
    items,
    isLoading,
    isError: messagesQuery.isError && conversationsQuery.isError,
    refetch,
  };
}
